import { 
  TextField, 
  InputAdornment,
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';

const FormInput = ({ 
  name, 
  label, 
  type = 'text', 
  placeholder,
  startIcon,
  endIcon,
  showPasswordToggle = false,
  validationRules = {},
  sx = {},
  ...props 
}) => {
  const { register, formState: { errors, touchedFields }, watch } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  
  const hasError = !!errors[name];
  const isTouched = touchedFields[name];
  const fieldValue = watch(name);
  const isValid = isTouched && !hasError && fieldValue?.length > 0;

  // Password toggle handler
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Determine input type
  const getInputType = () => {
    if (type === 'password' && showPasswordToggle) {
      return showPassword ? 'text' : 'password';
    }
    return type;
  };

  // Get end adornment
  const getEndAdornment = () => {
    if (endIcon) return endIcon;
    
    if (type === 'password' && showPasswordToggle) {
      return (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleTogglePasswordVisibility}
            edge="end"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              '&:hover': {
                color: '#00ff88',
              },
            }}
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      );
    }
    
    if (isValid && !hasError && type !== 'password') {
      return (
        <InputAdornment position="end">
          <Tooltip title="Valid">
            <CheckCircleIcon sx={{ color: '#00ff88', fontSize: '1.2rem' }} />
          </Tooltip>
        </InputAdornment>
      );
    }
    
    return null;
  };

  // Get default validation rules based on field type
  const getDefaultValidationRules = () => {
    const defaultRules = {
      required: `${label} is required`,
    };

    switch (type) {
      case 'email':
        return {
          ...defaultRules,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Please enter a valid email address"
          }
        };
      case 'password':
        return {
          ...defaultRules,
          minLength: {
            value: 6,
            message: "Password must be at least 6 characters"
          }
        };
      default:
        return defaultRules;
    }
  };

  const validation = { ...getDefaultValidationRules(), ...validationRules };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -2 }}
      style={{ width: '100%' }}
    >
      <TextField
        error={hasError}
        label={
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 600,
              fontSize: '1rem',
              mb: 1,
            }}
          >
            {label}
          </Typography>
        }
        type={getInputType()}
        variant="outlined"
        fullWidth
        {...register(name, validation)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: startIcon && (
            <InputAdornment position="start">
              {startIcon}
            </InputAdornment>
          ),
          endAdornment: getEndAdornment(),
          sx: {
            color: 'white',
            borderRadius: '12px',
            backgroundColor: 'transparent !important',
            background: 'none !important',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backgroundColor: 'transparent !important',
              background: 'none !important',
            },
            '&.Mui-focused': {
              border: '1px solid #00ff88',
              boxShadow: '0 0 0 3px rgba(0, 255, 136, 0.1)',
              backgroundColor: 'transparent !important',
              background: 'none !important',
            },
            '&.Mui-error': {
              border: '1px solid #ff3366',
              '&.Mui-focused': {
                boxShadow: '0 0 0 3px rgba(255, 51, 102, 0.1)',
              },
            },
            '& input': {
              padding: '14px 16px',
              fontSize: '0.95rem',
              backgroundColor: 'transparent',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.4)',
                opacity: 1,
              },
              // Remove browser autofill background so field matches unfilled state
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #001d3d inset',
                boxShadow: '0 0 0 1000px #001d3d inset',
                WebkitTextFillColor: '#fff',
                caretColor: 'white',
                transition: 'background-color 5000s ease-in-out 0s',
              },
              '&:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 1000px #001d3d inset',
                boxShadow: '0 0 0 1000px #001d3d inset',
              },
              '&:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 1000px #001d3d inset',
                boxShadow: '0 0 0 1000px #001d3d inset',
              },
              '&:-webkit-autofill:active': {
                WebkitBoxShadow: '0 0 0 1000px #001d3d inset',
                boxShadow: '0 0 0 1000px #001d3d inset',
              },
            },
            ...props.InputProps?.sx,
          },
        }}
        InputLabelProps={{
          sx: {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
        helperText={
          hasError ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <ErrorIcon sx={{ fontSize: '1rem', color: '#ff3366' }} />
              <Typography variant="caption" sx={{ color: '#ff3366' }}>
                {errors[name]?.message}
              </Typography>
            </Box>
          ) : null
        }
        FormHelperTextProps={{
          sx: {
            margin: '4px 0 0 0',
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent !important',
            background: 'none !important',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              backgroundColor: 'transparent !important',
              background: 'none !important',
            },
            '&.Mui-focused': {
              backgroundColor: 'transparent !important',
              background: 'none !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          },
          ...sx,
        }}
        {...props}
      />
    </motion.div>
  );
};

export default FormInput;